import { useCallback, useMemo, useState, type ReactNode } from 'react';
import cn from 'classnames';
import Header from './Header';
import Sidebar from './Sidebar';
import MobileSidebar from './MobileSidebar';
import Footer from './Footer';
import AccountBar from './AccountBar';
import SidebarRail from './SidebarRail';
import MobileTopBar from './MobileTopBar';
import MobileMenu from './MobileMenu';
import useScreenWidth from '@/shared/hooks/useScreenWidth';
import { useUser } from '@/entities/user/useUser';
import { useNotifications } from '@/entities/notification';
import { NotificationsDrawer } from '@/entities/notification';
import { NewsModal } from '@/features/news';
import { brand } from '@/brand';
import { usePageTitle } from '@/app/usePageTitle';
import { PageHeaderAccountContext } from '@/shared/lib/pageHeaderAccount';

// 1:1 с mcms-front MainLayout. Header (если бренд держит шапку сверху) +
// Sidebar/MobileSidebar + NotificationsDrawer + main с контентом + Footer.
//
// Раскладка шапки — структурное решение бренда (brand.layout.header), а не
// цвета: при 'in-sidebar' логотип, профиль, уведомления и профит живут внутри
// сайдбара, отдельного Header нет.
//
// Bootstrap-хуки useUser/useNotifications здесь, чтобы при mount layout-а
// /me и /me/notifications сразу заполнили RTK store. Дочерние компоненты
// (Header/Sidebar) читают данные через useAppSelector.
//
// useCountries/useOperators/usePlatforms из mcms-front НЕ переносим сейчас —
// эти entities понадобятся при миграции Streams/Sources/Stat-страниц,
// тогда и добавим хуки сюда же.

interface MainLayoutProps {
  className?: string;
  children: ReactNode;
}

// Сторона меню — flex-порядок, а не абсолютное позиционирование: сайдбар
// sticky/self-start и сам по себе позиции не задаёт. Скругление верхнего угла
// контента переезжает на ту сторону, где стыкуется с меню.
const isSidebarRight = brand.layout.sidebar === 'right';


const MainLayout: React.FC<MainLayoutProps> = ({ className, children }) => {
  const screenWidth = useScreenWidth();

  usePageTitle();
  useUser();
  useNotifications();

  // Шапка внутри колонки контента (макет telecom): строка аккаунта первой строкой
  // main, поля шире (40px по бокам, 32px сверху), контент встык с сайдбаром без
  // скругления угла — сайдбар идёт на всю высоту, стыку нечего сглаживать.
  // Читается в рендере, как и 'top' ниже: развилку проверяет тест с подменённым
  // брендом (app/layout/__tests__/layoutViews.test.tsx).
  const isHeaderInContent = brand.layout.header === 'in-content';
  // Навигация ниже lg (brand.layout.compactNav): у 'rail-topbar' на планшете
  // рейка иконок, на телефоне верхняя панель и меню на весь экран; у 'drawer' —
  // бургер в шапке страницы и шторка справа. Поля контента ниже lg — по макету
  // telecom (16/20px на телефоне, 24/28px на планшете).
  const isRailTopbar = brand.layout.compactNav === 'rail-topbar';

  // Строка аккаунта — в ряду с заголовком страницы (PageHeader забирает её через
  // контекст, см. shared/lib/pageHeaderAccount). Своей строкой над контентом
  // MainLayout рисует её, только пока шапки на экране нет. С какой ширины она
  // видна: у бренда с рейкой — с планшета, ниже верхняя панель с балансом.
  const accountBarShownFrom = isRailTopbar ? 'md:flex' : 'lg:flex';
  const [accountClaims, setAccountClaims] = useState(0);
  const claimAccount = useCallback(() => {
    setAccountClaims((count) => count + 1);
    return () => setAccountClaims((count) => count - 1);
  }, []);
  const accountValue = useMemo(
    () =>
      isHeaderInContent
        ? { slot: <AccountBar className={accountBarShownFrom} />, claim: claimAccount }
        : null,
    [isHeaderInContent, accountBarShownFrom, claimAccount],
  );

  const compactNav = isRailTopbar
    ? screenWidth < 768 && <MobileMenu />
    : screenWidth < 1024 && <MobileSidebar />;

  return (
    <>
      <div className="flex min-h-screen flex-col">
        {brand.layout.header === 'top' && <Header />}
        {isRailTopbar && <MobileTopBar />}
        <div
          className={cn(
            'relative flex w-full justify-between',
            isSidebarRight && 'flex-row-reverse',
          )}
        >
          <Sidebar />
          {isRailTopbar && <SidebarRail />}
          {compactNav}
          {brand.features.notifications && <NotificationsDrawer />}
          <NewsModal />
          {/* Footer теперь внутри main (flex-col, Footer прижат к низу через
              mt-auto). Это убирает overflow на Sidebar — раньше Footer был
              на одном уровне с inner-flex и заезжал на навбар. */}
          <main
            className={cn(
              'flex min-h-screen w-full min-w-0 flex-col bg-surface-page lg:min-h-[calc(100vh-3.5rem)] lg:bg-surface-page-alt',
              // Поле 16px на телефоне даёт сама страница (px-4 sm:px-0 у каждой), поэтому
              // здесь до sm его нет — иначе у telecom выходило двойное, 32px.
              //
              // Над main на телефоне стоит MobileTopBar (60px, в потоке), и min-h-screen
              // делал любую страницу на 60px выше окна — она прокручивалась, даже
              // будучи короткой.
              isRailTopbar
                ? 'px-0 pb-7 pt-5 max-md:min-h-[calc(100vh-60px)] sm:px-4 md:px-6 md:pt-7'
                : 'px-0 pb-4 pt-3 sm:px-4 sm:pb-2.5 sm:pt-3.5',
              isHeaderInContent
                ? 'lg:px-10 lg:pt-8'
                : cn('lg:px-6 lg:pt-6', isSidebarRight ? 'lg:rounded-tr-md' : 'lg:rounded-tl-md'),
              className,
            )}
          >
            {isHeaderInContent && accountClaims === 0 && (
              <AccountBar className={cn('mb-4', accountBarShownFrom)} />
            )}
            <PageHeaderAccountContext.Provider value={accountValue}>{children}</PageHeaderAccountContext.Provider>
            <Footer />
          </main>
        </div>
      </div>
    </>
  );
};

export default MainLayout;
